import type { PreferenceCategory } from '@elara/domain/models/schemas/shared-schemas';

import type { ChatMessage } from '#models/ChatMessageModel.js';

import { isWidgetPart } from '@elara/domain/models/schemas/message-schemas';
import { PreferenceProposalWidgetSchema } from '@elara/domain/models/schemas/widget-schemas';

/** A "remember this for future trips?" proposal still waiting for the guest's yes/no. */
export interface PendingPreferenceProposal {
  chatId: string;
  messageId: string;
  proposalId: string;
  category: PreferenceCategory;
  statement: string;
}

/**
 * Collects the undecided preference proposals in a chat, oldest first, so the composer can ask them
 * one at a time. Replies still generating are skipped: the server persists the card only once the
 * reply finishes, so deciding earlier would fail. Malformed cards are skipped rather than thrown on.
 */
export function findPendingPreferenceProposals(messages: readonly ChatMessage[]): PendingPreferenceProposal[] {
  return messages.flatMap((msg) => {
    if (!msg.isModelMessage || msg.isOptimistic || msg.isGenerating || !msg.chatId || !msg.id) return [];
    return (msg.content ?? []).flatMap((part) => {
      if (!isWidgetPart(part) || part.widget.type !== 'showPreferenceProposalWidget') return [];
      const proposal = PreferenceProposalWidgetSchema.safeParse(part.widget.payload).data?.data;
      if (proposal?.decision !== 'pending') return [];
      const { proposalId, category, statement } = proposal;
      return [{ chatId: msg.chatId, messageId: msg.id, proposalId, category, statement }];
    });
  });
}
